import './campaignGuide.scss';
// The stage tiles deliberately reuse the campaign wizard's own navigation styling so the guide matches the real screen.
import '../../subscriptionQuestionnaires/questionnaires/questionnaireNavigation.scss';

import { MouseEvent, useMemo, useState } from 'react';
import { Card, CardBody, Col, NavItem, NavLink, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ConditionalFragment } from 'react-conditionalfragment';
import { Background } from '../../shared/background/Background';
import { MainContainer } from '../../shared/MainContainer';
import { NavTiles } from '../../shared/NavTiles';
import { useReplaceSearchParamsEffect, useSearchParams } from '../../../shared/useURLSearchParams';
import { HelpBanner } from '../HelpBanner';
import { CampaignGuideHero } from './CampaignGuideHero';
import { CampaignGuideStage, getCampaignGuideStages } from './campaignGuideStages';

/**
 * Help page that walks a client through setting up their first campaign, stage by stage.
 */
export const CampaignGuide = () => {
    const { t } = useTranslation();

    const stages = useMemo(() => getCampaignGuideStages(t), [t]);

    // Remember the stage being viewed in the query string so a link can point straight at a stage.
    const { stage: stageFromUrl } = useSearchParams();
    const [currentStageIndex, setCurrentStageIndex] = useState<number>(() => Math.max(stages.findIndex(item => item.key === stageFromUrl), 0));
    const currentStage = stages[currentStageIndex];
    const searchParamChanges = useMemo(() => ({ stage: currentStage.key }), [currentStage.key]);
    useReplaceSearchParamsEffect(searchParamChanges);

    const reasons = useMemo(() => [
        {
            title: t('campaignGuide.why.didItWork.title', 'Did it work?'),
            text: t('campaignGuide.why.didItWork.text', 'Pre- and post-assessments in the Questionnaire stage turn "we ran the course" into measured knowledge gain.'),
        },
        {
            title: t('campaignGuide.why.didItStick.title', 'Did it stick?'),
            text: t('campaignGuide.why.didItStick.text', 'Scheduled follow-ups and reminders (Schedule stage) capture workplace application weeks after the session, not just on the day.'),
        },
        {
            title: t('campaignGuide.why.wasItWorthIt.title', 'Was it worth it?'),
            text: t('campaignGuide.why.wasItWorthIt.text', 'Correct People data, managers included, is what lets IMPACTS link outcomes back to costs, revenue and ROI later.'),
        },
    ], [t]);

    const reviewChecklist = useMemo(() => [
        t('campaignGuide.summary.checklist.name', 'Name, with the section and question count showing correctly'),
        t('campaignGuide.summary.checklist.participants', 'Participants, with the right number of people added'),
        t('campaignGuide.summary.checklist.dates', 'Start Date and End Date, as intended'),
        t('campaignGuide.summary.checklist.learningUnit', 'Learning Unit, linked if cost/ROI reporting is needed'),
    ], [t]);

    return (
        <Background>
            <HelpBanner hero={<CampaignGuideHero />} />
            <MainContainer className="campaign-guide" fluid>
                {/* Why campaigns exist */}
                <section className="campaign-guide-section">
                    <SectionHeading
                        kicker={t('campaignGuide.why.kicker', 'Why campaigns exist')}
                        title={t('campaignGuide.why.heading', 'Every stage answers a question the business will ask later')}
                        text={t('campaignGuide.why.text', 'The wizard isn\'t just data entry. Each stage feeds a specific proof point you\'ll need once training is over.')}
                    />
                    <Row>
                        {reasons.map((reason, index) => (
                            <Col key={reason.title} xs={12} md={4} className="mb-3">
                                <Card className="campaign-guide-reason h-100">
                                    <CardBody>
                                        <span className="campaign-guide-number">{formatStepNumber(index)}</span>
                                        <h3>{reason.title}</h3>
                                        <p className="mb-0">{reason.text}</p>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </section>

                {/* The setup stages */}
                <section className="campaign-guide-section">
                    <SectionHeading
                        kicker={t('campaignGuide.stages.kicker', 'Five stages')}
                        title={t('campaignGuide.stages.heading', 'Create a campaign')}
                        text={t('campaignGuide.stages.text', 'These are the same stages you\'ll see across the top of the campaign screen. Pick one to see what it asks for.')}
                    />
                    <div className="questionnaire-navigation-container campaign-guide-stage-tiles">
                        <StageTiles stages={stages} currentStageIndex={currentStageIndex} onSelect={setCurrentStageIndex} />
                    </div>

                    <StageDetails
                        stages={stages}
                        currentStageIndex={currentStageIndex}
                        onSelect={setCurrentStageIndex}
                    />
                </section>

                {/* Summary */}
                <section className="campaign-guide-section">
                    <div className="campaign-guide-summary">
                        <Row>
                            <Col xs={12} lg={6}>
                                <h2>{t('campaignGuide.summary.heading', 'Before you hit Send')}</h2>
                                <p>
                                    {t('campaignGuide.summary.text', 'Once every stage shows complete, Review & Send gives you a final read-through. After launch, IMPACTS takes over: collecting assessments, chasing reminders, and building your ROI picture automatically.')}
                                </p>
                                <p>
                                    {t('campaignGuide.summary.buttons', 'The buttons at the bottom of the campaign screen let you Save & Close, Schedule launch, Preview or Close.')}
                                </p>
                            </Col>
                            <Col xs={12} lg={6}>
                                <ul className="campaign-guide-summary-checklist">
                                    {reviewChecklist.map((item, index) => (
                                        <li key={item}>
                                            <span className="campaign-guide-summary-checklist-number">{formatStepNumber(index)}</span>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </Col>
                        </Row>
                    </div>
                </section>
            </MainContainer>
        </Background>
    );
};

/**
 * Formats a zero based index as a two digit step number, for example 0 becomes "01".
 */
const formatStepNumber = (index: number) => `${index + 1}`.padStart(2, '0');

interface SectionHeadingProps {
    kicker: string,
    title: string,
    text: string,
}

/**
 * Centred heading used at the top of each section of the guide.
 */
const SectionHeading = (props: SectionHeadingProps) => {
    const { kicker, title, text } = props;

    return (
        <div className="campaign-guide-section-heading">
            <div className="campaign-guide-kicker">{kicker}</div>
            <h2>{title}</h2>
            <p className="text-muted">{text}</p>
        </div>
    );
};

interface StageTilesProps {
    stages: Array<CampaignGuideStage>,
    currentStageIndex: number,
    onSelect: (stageIndex: number) => void,
}

/**
 * Stage tiles styled like the campaign wizard's navigation. Stages before the current one show as complete, as they do in the wizard.
 */
const StageTiles = (props: StageTilesProps) => {
    const { stages, currentStageIndex, onSelect } = props;

    return (
        <NavTiles className="nav-tiles-large">
            {stages.map((stage, index) => (
                <NavItem key={stage.key}>
                    <NavLink
                        href="#"
                        className={`${stage.key}-navlink ${index === currentStageIndex ? 'active' : 'nav-link-notactive'}`}
                        aria-current={index === currentStageIndex ? 'step' : undefined}
                        onClick={(e: MouseEvent) => { e.preventDefault(); onSelect(index); }}
                    >
                        <ConditionalFragment showIf={index < currentStageIndex}>
                            <FontAwesomeIcon icon="check-circle" className="completed-icon" /><> </>
                        </ConditionalFragment>
                        {stage.label}
                    </NavLink>
                </NavItem>
            ))}
        </NavTiles>
    );
};

interface StageDetailsProps {
    stages: Array<CampaignGuideStage>,
    currentStageIndex: number,
    onSelect: (stageIndex: number) => void,
}

/**
 * Explanation of the current setup stage, with a mock-up of what the stage looks like once filled in, and buttons to move between stages.
 */
const StageDetails = (props: StageDetailsProps) => {
    const { stages, currentStageIndex, onSelect } = props;
    const { t } = useTranslation();

    const stage = stages[currentStageIndex];
    const previousStage = stages[currentStageIndex - 1];
    const nextStage = stages[currentStageIndex + 1];

    return (
        <div className="campaign-guide-stage">
            <Row noGutters>
                <Col xs={12} lg={7} className="campaign-guide-stage-copy order-2 order-lg-1">
                    <div className="campaign-guide-stage-tag">
                        <span className="campaign-guide-stage-tag-dot" />
                        {t('campaignGuide.stageOf', 'Stage {{stageNumber}} of {{stageCount}}', { stageNumber: currentStageIndex + 1, stageCount: stages.length })}
                    </div>
                    <h3>{stage.title}</h3>
                    <p className="campaign-guide-stage-introduction">{stage.introduction}</p>
                    <ul className="campaign-guide-field-list">
                        {stage.fields.map(field => (
                            <li key={field.name}>
                                <span className="campaign-guide-field-check">
                                    <FontAwesomeIcon icon="check" />
                                </span>
                                <div>
                                    <div className="campaign-guide-field-name">{field.name}</div>
                                    <div className="campaign-guide-field-description">{field.description}</div>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <div className="campaign-guide-tip">
                        <FontAwesomeIcon icon="lightbulb" className="campaign-guide-tip-icon" />
                        <span>{stage.tip}</span>
                    </div>
                </Col>
                <Col xs={12} lg={5} className="campaign-guide-stage-visual order-1 order-lg-2">
                    {/* A simplified mock-up of the screen, to give an idea of what the stage asks for. */}
                    <div className="campaign-guide-mock" aria-hidden="true">
                        <div className="campaign-guide-mock-bar">
                            <span /><span /><span />
                        </div>
                        <div className="campaign-guide-mock-body">
                            {stage.previewRows.map(row => (
                                <div key={row.text} className={`campaign-guide-mock-row ${row.isFilledIn ? 'campaign-guide-mock-row-filled' : ''}`}>
                                    <span>{row.text}</span>
                                    <ConditionalFragment showIf={row.isFilledIn}>
                                        <span className="campaign-guide-mock-row-bar" />
                                    </ConditionalFragment>
                                </div>
                            ))}
                        </div>
                    </div>
                </Col>
            </Row>
            <div className="campaign-guide-stage-footer">
                <button type="button" className="campaign-guide-stage-footer-button" disabled={!previousStage} onClick={() => onSelect(currentStageIndex - 1)}>
                    <FontAwesomeIcon icon="chevron-left" />
                    <> </>
                    {
                        previousStage ?
                            t('campaignGuide.previousStage', 'Previous: {{label}}', { label: previousStage.label })
                            : t('campaignGuide.previous', 'Previous')
                    }
                </button>
                <div className="campaign-guide-stage-progress" aria-hidden="true">
                    {stages.map((item, index) => (
                        <span key={item.key} className={index === currentStageIndex ? 'active' : ''} />
                    ))}
                </div>
                <button type="button" className="campaign-guide-stage-footer-button" disabled={!nextStage} onClick={() => onSelect(currentStageIndex + 1)}>
                    {
                        nextStage ?
                            t('campaignGuide.nextStage', 'Next: {{label}}', { label: nextStage.label })
                            : t('campaignGuide.next', 'Next')
                    }
                    <> </>
                    <FontAwesomeIcon icon="chevron-right" />
                </button>
            </div>
        </div>
    );
};
